import { useLayoutEffect, useRef, useState } from "react";

function isInViewport(node) {
  const rect = node.getBoundingClientRect();
  return rect.top < window.innerHeight && rect.bottom > 0;
}
export default function FadeInSection({ children, className = "", direction = "up", alwaysAnimate = false, skipDelayOnScroll = false, playOnLoad = false }) {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [revealedOnScroll, setRevealedOnScroll] = useState(false);

  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return;

    // Play on page load regardless of scroll position — for elements that are
    // always on screen (e.g. the sticky nav), whose hidden offset may push them
    // out of the viewport so the observer would never fire. Force a layout so
    // the hidden state is applied, then reveal on a timer (requestAnimationFrame
    // is paused in background tabs) so the transition runs.
    if (playOnLoad) {
      node.getBoundingClientRect();
      const timer = setTimeout(() => setIsVisible(true), 50);
      return () => clearTimeout(timer);
    }

    if (!alwaysAnimate && isInViewport(node)) {
      setIsVisible(true);
      return;
    }

    // The observer's first callback reports the state at load. If the section
    // starts fading in then, keep its delay (stagger); if it only reaches the
    // threshold later, the user scrolled to it, so skip the delay.
    let isInitialCallback = true;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setRevealedOnScroll(!isInitialCallback);
          setIsVisible(true);
          observer.disconnect();
        }
        isInitialCallback = false;
      },
      { threshold: 0.25 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [alwaysAnimate, playOnLoad]);

  const hiddenTransform =
    direction === "right"
      ? "translate-x-24"
      : direction === "left"
        ? "-translate-x-24"
        : direction === "down"
          ? "-translate-y-24"
          : "translate-y-24";

  return (
    <div
      ref={ref}
      className={`transition-all duration-[2000ms] ease-out ${
        isVisible ? "opacity-100 translate-x-0 translate-y-0" : `opacity-0 ${hiddenTransform}`
      } ${className}`}
      // Delay classes are meant for staggering content already on screen at load;
      // when revealed later by scrolling, show it without the extra wait.
      style={skipDelayOnScroll && revealedOnScroll ? { transitionDelay: "0ms" } : undefined}
    >
      {children}
    </div>
  );
}
